import React from 'react';

/**
 * BrioMark — the practice mark: a globe that reads as a clock.
 *
 *   circle      the sphere, and the bezel of a dial
 *   horizontal  the equator, whose right half is the hour hand at three
 *   upright     the minute hand at twelve
 *
 * One stroke does two jobs, which is the whole idea. The upright stops short
 * of the equator, and that gap is what turns a letter T into a hand hovering
 * over a dial — without it the mark reads as a crosshair. Raise HAND_FOOT
 * toward 12 to close the gap, lower it toward 9.8 to open it.
 *
 * Geometry sits on lucide's 24 unit box with round caps and joins, and the
 * stroke is `currentColor`, so this is a drop-in for a lucide icon: size with
 * the `size` prop, colour with a CSS class or `style.color`.
 *
 * The home screen and favicon artwork is the same drawing, generated from
 * scripts/generate-app-icons.py. Change the geometry in both or they drift.
 */
const HAND_TIP = 5.5;
const HAND_FOOT = 10.2;

/**
 * A single stroke weight cannot serve 16px and 180px: at 1.25 a favicon
 * dissolves to grey, at 2 the large mark is heavy. Weight follows size.
 */
function strokeFor(size) {
  if (size >= 64) return 1.25;
  if (size >= 32) return 1.5;
  if (size >= 20) return 1.75;
  return 2;
}

export default function BrioMark({
  size = 24,
  strokeWidth,
  label,
  className,
  style,
}) {
  const titled = Boolean(label);
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? strokeFor(size)}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={titled ? 'img' : undefined}
      aria-label={titled ? label : undefined}
      aria-hidden={titled ? undefined : true}
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d={`M12 ${HAND_FOOT}V${HAND_TIP}`} />
    </svg>
  );
}
